import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import styled, { CSSObject, css } from 'styled-components';
import {
  Table,
  TableIcon,
  SelectInput,
  ActionIconContainer,
  useStickySafeNavigate,
  Card,
  Flex,
  useStyledTheme,
  FlexColumn,
  SecondaryButton,
  AlertModal,
  Button,
  TextSort,
  EmptyData,
  StatusContainer,
  StatusContainerSuccess,
  IconButton,
  TextareaInput,
  Tooltip,
  MultiSelectInput,
  DateSort,
  useIsXOverflow,
  SubHeaderContainerProps,
  useAsyncFunctionProgress,
  SearchSelectBox,
  useCombinedUser,
} from '@equifinance/shared';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils/datetime';
import { uniq, cloneDeep, set } from 'lodash';
import { Link } from 'react-router-dom';
import { OtherReason, WithdrawReasonType } from '../../api/useAccountApi';
import {
  Case,
  CaseBrokerStatuses,
  CaseStatuses,
  useCasesContext,
} from '../../contexts/cases';
import { Assignee, useAssigneesContext } from '../../contexts/assignees';
import { useCasesUIContext } from '../../contexts/casesUI/CasesUIProvider';
import { useCacheFilters } from '../../hooks/useCacheFilters';

export const statusFilterCss = css` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  .dropdown-container, .multi-select-parent {
    height: ${theme.padding[8]};
    font-size: ${theme.fontSize[1]};
    font-weight: ${theme.fontWeight.bold};
  }
  .multi-select-parent {
    min-width: auto;
  }
  .dropdown-container {
    border-radius: ${theme.padding[2]};
    border: 1px solid ${theme.colours.neutral[6]};
  }
  .mi-chevron-up, .mi-chevron-down {
    font-size: ${theme.fontSize[4]};
  }
  .dropdown-content {
    min-width: ${theme.dropdownWidth};
  }
  .mi-close {
    font-size: ${theme.fontSize[3]};
    display: flex;
  }
`}`;

export const Container = styled.div`${({ theme }) => `
  width:100%;
  margin-left: ${theme.padding[8]};
  margin-right: ${theme.padding[8]};
`}`;

export const CardContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[4]};
`}`;

export const SubHeaderContainer = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  justify-content: space-between;
  width: 100%;
  padding-bottom: ${theme.padding[4]};
`}`;

export const SubHeaderFilters = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
  flex-wrap: wrap;
`}`;

const SelectApplicationContainer = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
  justify-content: center;
`}`;

const SelectApplicationButton = styled(SecondaryButton) <{ selected: boolean }>`${({ theme, selected }) => `
  background: ${selected ? theme.colours.primary[11] : theme.colours.neutral[10]};
  color: ${selected ? theme.colours.primary[1] : theme.colours.neutral[2]};
  border-color: ${selected ? theme.colours.primary[11] : theme.colours.neutral[6]};
  height: 32px;
  font-weight: ${theme.fontWeight.bold};
  padding: ${theme.padding[1]} ${theme.padding[4]};
`}`;

const AddApplicationButton = styled(SecondaryButton)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  border-color: ${theme.colours.primary[1]};
  height: 32px;
  flex-shrink: 0;
`}`;

const AddApplicationIcon = styled(TableIcon)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
`}`;

export const WithdrawModalContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[5]};
`}`;

export const WithdrawModalButtonContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  justify-content: end;
`}`;

export const ActionButton = styled(IconButton)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  ${TableIcon} {
    font-size: ${theme.fontSize[3]};
  }
`}`;

const ApplicantNameContainer = styled.div`
  max-width: 100%;
`;

const ApplicantNameText = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ErrorList = styled.div<{ isBulletShown: boolean }>`${({ theme, isBulletShown = false }) => `
  color: ${theme.textDanger};
  font-size: ${theme.fontSize[1]}
  margin: 0;
  padding-left: ${isBulletShown ? theme.padding[3] : 0};
  list-style: ${isBulletShown ? 'default' : 'none'};
  font-size: ${theme.fontSize[1]};
`}`;

const PlainLink = styled(Link)`
  text-decoration: none;
`;

const mutateSearchValue = (value: string) => {
  const allowedChars = /[A-Za-z0-9\-\\/ ]/g;
  const filtered = value.match(allowedChars);
  return filtered ? filtered.join('') : '';
};

function Applicant({ name, id }: { name: string, id: string }) {
  const nameRef = useRef<HTMLDivElement>(null);

  const isTruncated = useIsXOverflow(nameRef);

  return (
    <ApplicantNameContainer id={`credit-item-name-${id}`}>
      <ApplicantNameText ref={nameRef}>
        {name}
      </ApplicantNameText>
      {isTruncated && (
        <Tooltip
          id={`credit-item-name-${id}`}
          html={name}
          place="top"
        />
      )}
    </ApplicantNameContainer>
  );
}

const getAssigneeName = (assignee?: Assignee) => `${assignee?.firstName || ''} ${assignee?.surname || ''}`.trim();

function AssigneeSelect(account: Case) {
  const { updateBrokerOwner } = useCasesContext();
  const { assignees } = useAssigneesContext();

  const { id, brokerAssignedToId, errors: accountErrors } = account;
  const [errors, setErrors] = useState<string[]>([]);

  const combineErrors = (error?: Record<string, string[]>) => {
    if (!error) return [];
    return uniq(Object.values(error).flat());
  };

  const onChangeAssignee = async (brokerId: string) => {
    setErrors([]);
    const assignee = assignees.find((assign) => assign.brokerUserId === brokerId);

    if (!assignee || brokerAssignedToId === brokerId) return;
    const updatedCase = await updateBrokerOwner(id, assignee.brokerUserId, getAssigneeName(assignee) || null);
    setErrors(combineErrors(updatedCase?.errors));
  };

  useEffect(() => {
    setErrors(combineErrors(accountErrors));
  }, [id, accountErrors]);

  return (
    <SearchSelectBox
      isTable
      id={`select-broker-${id}`}
      value={brokerAssignedToId}
      options={
        assignees
          .filter((assignee) => (
            assignee.brokerUserId === brokerAssignedToId
            || assignee.statusIsActive
          ))
          .reduce((prev, current) => ({
            ...prev,
            [current.brokerUserId]: getAssigneeName(current),
          }), {})
      }
      errors={errors}
      onChangeItem={(value: string) => onChangeAssignee(value)}
      isTooltipError
      touchOverride
    />
  );
}

const defaultWithdrawError: {
  reason: string[],
  otherDescription: string[],
  otherErrors: string[]
} = {
  reason: [],
  otherDescription: [],
  otherErrors: [],
};

function WithdrawModalContent({ caseId, close }: { caseId: string, close: () => void }) {
  const {
    withdrawCase, getWithdrawalReasons,
  } = useCasesContext();

  const [withdrawError, setWithdrawError] = useState(defaultWithdrawError);
  const [withdrawReasons, setWithdrawReasons] = useState<{
    key: string,
    value: string
  }[]>([]);

  const [withdrawReason, SetWithdrawReason] = useState<Required<WithdrawReasonType>>({ reason: '', otherDescription: '' });
  const canWithdraw = withdrawReason.reason && (withdrawReason.reason !== OtherReason || withdrawReason.otherDescription !== '');
  const showWithdrawReason = withdrawReason.reason === OtherReason;

  const onWithdrawCase = async () => {
    close();
    setWithdrawError(defaultWithdrawError);
    const response = await withdrawCase(caseId, {
      reason: withdrawReason.reason,
      otherDescription: showWithdrawReason ? withdrawReason.otherDescription : null,
    });
    if (typeof response === 'string') {
      setWithdrawError((prev) => ({
        ...prev,
        otherErrors: [response],
      }));
      return;
    }
    if (response.errors) {
      const responseErrors = Object.entries(response.errors).reduce((prev, [key, errors]) => {
        if (['reason', 'otherDescription'].includes(key)) {
          set(prev, key, errors);
        } else {
          prev.otherErrors = uniq([...prev.otherErrors, ...errors]);
        }
        return prev;
      }, cloneDeep(defaultWithdrawError));
      setWithdrawError(responseErrors);
    }
  };

  useEffect(() => {
    (async () => {
      setWithdrawReasons(await getWithdrawalReasons());
    })();
  }, []);

  return (
    <WithdrawModalContainer>
      <SelectInput
        id="withdraw-case-reason-select"
        value={withdrawReason.reason}
        options={withdrawReasons.reduce((prev, current) => ({
          ...prev,
          [current.key]: current.value,
        }), {})}
        onChange={(value) => SetWithdrawReason((prev) => ({ ...prev, reason: value }))}
        label="Reason for withdrawal"
        errors={withdrawError.reason}
        touchOverride
        required
      />
      {showWithdrawReason && (
        <TextareaInput
          id="withdraw-case--other-reason-input"
          value={withdrawReason.otherDescription || ''}
          onChange={(value) => SetWithdrawReason((prev) => ({ ...prev, otherDescription: value }))}
          rows={1}
          label="Please provide details"
          errors={withdrawError.otherDescription}
          required
        />
      )}
      {!!withdrawError.otherErrors.length && (
        <ErrorList isBulletShown={withdrawError.otherErrors.length > 1}>
          {withdrawError.otherErrors.map((errorMessage) => (
            <li key={errorMessage}>{errorMessage}</li>
          ))}
        </ErrorList>
      )}
      <WithdrawModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button small onClick={onWithdrawCase} disabled={!canWithdraw}>Withdraw</Button>
      </WithdrawModalButtonContainer>
    </WithdrawModalContainer>
  );
}

function ReactivateModalContent({ caseId, close }: { caseId: string, close: () => void }) {
  const [reopenErrors, setReopenErrors] = useState<string[]>([]);
  const { reopenCase } = useCasesContext();

  const onReopenCase = async () => {
    close();
    const response = await reopenCase(caseId);

    if (typeof response === 'string') {
      setReopenErrors([response]);
      return;
    }

    if (response.errors) {
      const responseErrors = Object.values(response.errors).flatMap((errors) => errors);
      setReopenErrors(responseErrors);
    }
  };

  return (
    <WithdrawModalContainer>
      <p>Are you sure you want to reactivate this application?</p>
      {!!reopenErrors.length && (
        <ErrorList isBulletShown={reopenErrors.length > 1}>
          {reopenErrors.map((errorMessage) => (
            <li key={errorMessage}>{errorMessage}</li>
          ))}
        </ErrorList>
      )}
      <WithdrawModalButtonContainer>
        <SecondaryButton small onClick={close}>Cancel</SecondaryButton>
        <Button small onClick={onReopenCase}>Reactivate</Button>
      </WithdrawModalButtonContainer>
    </WithdrawModalContainer>
  );
}

function SubHeader({ searchComponent, children }: SubHeaderContainerProps) {
  const {
    createCase,
  } = useCasesContext();
  const navigate = useStickySafeNavigate();
  const theme = useStyledTheme();

  const createNewApplication = async () => {
    const { id } = (await createCase()) ?? {};
    navigate(`application/${id}`);
  };

  const { inProgress, execute } = useAsyncFunctionProgress(createNewApplication);

  return (
    <SubHeaderContainer>
      <SubHeaderFilters>
        {searchComponent}
        {children}
      </SubHeaderFilters>
      <AddApplicationButton
        disabled={inProgress}
        onClick={execute}
        fontSize={theme.fontSize[1]}
        small
      >
        <AddApplicationIcon className="mi-add" />
        New application
      </AddApplicationButton>
    </SubHeaderContainer>
  );
}

type ExternalLandingPageFilterSettings = {
  onlyMyApplications: boolean,
  statusFilter: string[];
}

const defaultFilters: ExternalLandingPageFilterSettings = {
  onlyMyApplications: false,
  statusFilter: [],
};

function CasesTable() {
  const { user } = useCombinedUser();
  const { cases } = useCasesContext();

  const { assignees } = useAssigneesContext();

  const {
    key,
    loading,
    onlyMyApplications,
    filterStatus,
    refreshData,
    setOnlyMyApplications,
    setFilterStatus,
  } = useCasesUIContext();

  const { filters, updateFilters } = useCacheFilters<ExternalLandingPageFilterSettings>({
    pageKey: 'external-portal-landing-page',
    userId: user?.id || '',
    defaultFilters,
  });

  // Restore Cached filters settings
  useEffect(() => {
    const { onlyMyApplications, statusFilter } = filters;
    setOnlyMyApplications(onlyMyApplications);
    setFilterStatus(statusFilter);
  }, []);

  useLayoutEffect(() => {
    refreshData();
  }, []);

  const deafultShowWithdrawModal = { id: '', open: false };
  const [showWithdrawModal, SetShowWithdrawModal] = useState<{ id: string, open: boolean }>(deafultShowWithdrawModal);
  const onClickWithdraw = (id: string) => () => {
    SetShowWithdrawModal({ id, open: true });
  };

  const deafultShowReactivateModal = { id: '', open: false };
  const [showReactivateModal, SetShowReactivateModal] = useState<{ id: string, open: boolean }>(deafultShowReactivateModal);
  const onClickReactivate = (id: string) => () => {
    SetShowReactivateModal({ id, open: true });
  };

  const ActionButtons = useCallback(
    ({ id, brokerStatus, status }: Case) => (
      <ActionIconContainer>
        {brokerStatus === CaseBrokerStatuses.WITHDRAWN && (
          <ActionButton data-testid={`btn-reactivate-${id}`} onClick={onClickReactivate(id)}>
            <TableIcon className="mi-refresh" />
          </ActionButton>
        )}
        {status === CaseStatuses.WITH_BROKER && (
          <ActionButton data-testid={`btn-withdraw-${id}`} onClick={onClickWithdraw(id)}>
            <TableIcon className="mi-delete" />
          </ActionButton>
        )}
        <PlainLink to={`/application/${id}`} data-testid={`btn-application-${id}`}>
          <ActionButton>
            <TableIcon className="mi-circle-arrow-right" />
          </ActionButton>
        </PlainLink>
      </ActionIconContainer>
    ),
    [cases],
  );

  const Status = useCallback(
    ({ brokerStatus }: Case) => (
      <>
        {(() => {
          switch (brokerStatus) {
            case CaseBrokerStatuses.WITHDRAWN: return (
              <StatusContainer>
                {brokerStatus}
              </StatusContainer>
            );
            case CaseBrokerStatuses.COMPLETED: return (
              <StatusContainerSuccess>
                {brokerStatus}
              </StatusContainerSuccess>
            );
            default:
              return brokerStatus;
          }
        })()}
      </>
    ),
    [cases],
  );

  const AssigneeSort = (rowA: Case, rowB: Case) => {
    const assigneeA = assignees.find((assignee) => assignee.brokerUserId === rowA.brokerAssignedToId);
    const assigneeB = assignees.find((assignee) => assignee.brokerUserId === rowB.brokerAssignedToId);

    const nameA = assigneeA ? `${assigneeA?.firstName} ${assigneeA?.surname}` : '';
    const nameB = assigneeB ? `${assigneeB?.firstName} ${assigneeB?.surname}` : '';

    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  };

  const columns = [
    {
      name: 'Reference',
      sortable: true,
      selector: (account: Case) => account.reference,
      sortFunction: TextSort<Case>('reference'),
    },
    {
      name: 'Applicant',
      sortable: true,
      cell: (account: Case) => Applicant({
        name: account.applicantNames,
        id: account.id,
      }),
      sortFunction: TextSort<Case>('applicantNames'),
    },
    {
      name: 'Postcode',
      sortable: true,
      selector: (account: Case) => account.securityAddressPostCode,
      sortFunction: TextSort<Case>('securityAddressPostCode'),
    },
    {
      name: 'Status',
      sortable: true,
      cell: (account: Case) => Status(account),
      sortFunction: TextSort<Case>('brokerStatus'),
    },
    {
      name: 'Broker case owner',
      sortable: true,
      cell: (account: Case) => AssigneeSelect(account),
      sortFunction: AssigneeSort,
    },
    {
      name: 'Underwriter',
      sortable: true,
      cell: (account: Case) => Applicant({
        name: account.underwriterAssignedToName || '-',
        id: `${account.id}-${account.underwriterAssignedToName}`,
      }),
      sortFunction: TextSort<Case>('underwriterAssignedToName'),
    },
    {
      name: 'Tasks',
      sortable: true,
      selector: (account: Case) => account.totalTasksOutstanding ?? 'N/A',
    },
    {
      name: 'Last updated',
      sortable: true,
      cell: (account: Case) => formatDateTime(account.lastUpdated, DateFormatStringEnum.YYYYMMDDHHmmss),
      sortFunction: DateSort<Case>('lastUpdated'),
    },
    {
      name: '',
      sortable: false,
      cell: (account: Case) => ActionButtons(account),
    },
  ];

  const theme = useStyledTheme();

  const stickyColumn = {
    style: {
      '&:is(:last-of-type)': {
        position: 'sticky',
        right: 0,
      },
    } as CSSObject,
  };

  const casesWithBrokerNames = useMemo(() => cases.map((c) => ({
    ...c,
    brokerAssignedToName: getAssigneeName(assignees.find((a) => a.brokerUserId === c.brokerAssignedToId)),
  })), [cases, assignees]);

  const filteredCases = useMemo(() => casesWithBrokerNames.filter((caseValue) => {
    if (!!filterStatus.length && !filterStatus.includes(caseValue.brokerStatus)) return false;
    if (onlyMyApplications) return caseValue.isCurrentBroker;
    return true;
  }), [onlyMyApplications, filterStatus, casesWithBrokerNames]);

  const subHeaderComponent = (
    <>
      <SelectApplicationContainer>
        <SelectApplicationButton
          onClick={() => {
            setOnlyMyApplications(false);
            updateFilters({ onlyMyApplications: false });
          }}
          selected={!onlyMyApplications}
          fontSize={theme.fontSize[1]}
          disabled={!cases.length}
          small
        >
          All applications
        </SelectApplicationButton>
        <SelectApplicationButton
          onClick={() => {
            setOnlyMyApplications(true);
            updateFilters({ onlyMyApplications: true });
          }}
          selected={onlyMyApplications}
          fontSize={theme.fontSize[1]}
          disabled={!cases.length}
          small
        >
          My applications
        </SelectApplicationButton>
      </SelectApplicationContainer>
      <MultiSelectInput
        id="case-status-filter"
        onChange={(value: string[]) => {
          setFilterStatus(value);
          updateFilters({ statusFilter: value });
        }}
        value={filterStatus}
        selectedLabel={filterStatus.length ? 'Status:' : 'Status'}
        options={Object.values(CaseBrokerStatuses).reduce((a, v) => ({ ...a, [v]: v }), {})}
        onDeselectOption={(value: string) => {
          const newValues = filterStatus.filter((id) => id !== value);
          setFilterStatus(newValues);
          updateFilters({ statusFilter: newValues });
        }}
        disabled={!cases.length}
        containerStyles={statusFilterCss}
      />
    </>
  );

  return (
    <>
      <AlertModal
        title="Withdraw the application"
        content={(
          <WithdrawModalContent
            caseId={showWithdrawModal.id}
            close={() => SetShowWithdrawModal(deafultShowWithdrawModal)}
          />
        )}
        isOpen={showWithdrawModal.open}
        onClose={() => SetShowWithdrawModal(deafultShowWithdrawModal)}
        showButtons={false}
      />

      <AlertModal
        title="Reactivate the application"
        content={(
          <ReactivateModalContent
            caseId={showReactivateModal.id}
            close={() => SetShowReactivateModal(deafultShowReactivateModal)}
          />
        )}
        isOpen={showReactivateModal.open}
        onClose={() => SetShowReactivateModal(deafultShowReactivateModal)}
        showButtons={false}
      />
      <Container>
        <Card>
          <CardContainer>

            <Table
              key={key}
              columns={columns}
              data={filteredCases}
              styles={{
                headCells: stickyColumn,
                cells: stickyColumn,
              }}
              SubHeaderContainer={SubHeader}
              subHeaderComponent={subHeaderComponent}
              pagination
              defaultSortFieldId={8}
              defaultSortAsc={false}
              progressPending={loading}
              isFiltered={onlyMyApplications || !!filterStatus.length}
              noDataComponent={<EmptyData message="Please start a new record." />}
              searchInputProps={{
                mutateValue: mutateSearchValue,
              }}
            />
          </CardContainer>
        </Card>
      </Container>
    </>
  );
}

export default CasesTable;
